// WordPress dependencies
import {
	Panel,
	PanelBody,
	PanelRow,
	RangeControl,
	ToggleControl,
	SelectControl,
	__experimentalNumberControl as NumberControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';

// Internal dependencies
import CarouselColorControl from '../CarouselColorControl';

/**
 * Settings Tab Component
 */
// Default values for settings
const DEFAULTS = {
	speed: 300,
	loop: true,
	autoplay: false,
	autoplay_delay: 3000,
	pause_on_hover: true,
	navigation: true,
	pagination: true,
	pagination_type: 'bullets',
	// Navigation style defaults
	nav_bg_color: '',
	nav_arrow_color: '',
	nav_hover_bg_color: '',
	nav_hover_arrow_color: '',
	// Pagination style defaults
	pag_bg_color: '',
	pag_active_color: '',
	pag_hover_color: '',
	// Responsive defaults
	breakpoint_mobile: 640,
	slides_mobile: 1,
	space_mobile: 10,
	breakpoint_tablet: 768,
	slides_tablet: 2,
	space_tablet: 20,
	breakpoint_desktop: 1024,
	slides_desktop: 3,
	space_desktop: 30,
};

/**
 * Breakpoint Row Component for responsive settings
 */
const BreakpointRow = ( {
	title,
	description,
	breakpointLabel,
	breakpointValue,
	breakpointMin,
	breakpointMax,
	onBreakpointChange,
	slidesValue,
	slidesMax,
	onSlidesChange,
	spaceValue,
	onSpaceChange,
} ) => {
	return (
		<div className="bais-breakpoint-row">
			<h4>{ title }</h4>
			{ description && <p className="description">{ description }</p> }

			<div className="bais-breakpoint-fields">
				<div className="bais-field">
					<label>{ breakpointLabel }</label>
					<div className="bais-field-input">
						<NumberControl
							value={ breakpointValue }
							onChange={ onBreakpointChange }
							min={ breakpointMin }
							max={ breakpointMax }
							step={ 1 }
						/>
						<span className="unit">px</span>
					</div>
				</div>

				<div className="bais-field">
					<label>{ __( 'Sliders per carousel', 'wp-before-after-image-slider' ) }</label>
					<NumberControl
						value={ slidesValue }
						onChange={ onSlidesChange }
						min={ 1 }
						max={ slidesMax }
						step={ 1 }
					/>
				</div>

				<div className="bais-field">
					<label>{ __( 'Space (px)', 'wp-before-after-image-slider' ) }</label>
					<NumberControl
						value={ spaceValue }
						onChange={ onSpaceChange }
						min={ 0 }
						max={ 100 }
						step={ 1 }
					/>
				</div>
			</div>
		</div>
	);
};

const SettingsTab = ( props ) => {
	const { attributes, updateAttributes, proFeatureEnabled } = props;

	// Helper to get value with fallback to default
	const getValue = ( key ) => {
		const val = attributes[ key ];
		// For boolean fields, explicitly check
		if ( typeof DEFAULTS[ key ] === 'boolean' ) {
			if ( typeof val === 'boolean' ) {
				return val;
			}
			// Handle string "true"/"false" or "1"/"0"
			if ( val === 'true' || val === '1' || val === 1 ) {
				return true;
			}
			if ( val === 'false' || val === '0' || val === 0 || val === '' ) {
				return false;
			}
			return DEFAULTS[ key ];
		}
		return val !== undefined && val !== null && val !== '' ? val : DEFAULTS[ key ];
	};

	return (
		<Panel>
			{/* Basic Settings */}
			<PanelBody
				title={ __( 'Basic Settings', 'wp-before-after-image-slider' ) }
				initialOpen={ true }
			>
				<PanelRow>
					<RangeControl
						label={ __( 'Transition Speed (ms)', 'wp-before-after-image-slider' ) }
						help={ __( 'Duration of transition between slides in milliseconds.', 'wp-before-after-image-slider' ) }
						value={ getValue( 'speed' ) }
						onChange={ ( value ) => updateAttributes( { speed: value } ) }
						min={ 100 }
						max={ 3000 }
						step={ 100 }
						withInputField={ true }
						renderTooltipContent={ ( value ) => `${ value }ms` }
					/>
				</PanelRow>

				<PanelRow>
					<ToggleControl
						label={ __( 'Loop Mode', 'wp-before-after-image-slider' ) }
						help={ __( 'Automatically loops back to the first slide after the last one.', 'wp-before-after-image-slider' ) }
						checked={ Boolean( getValue( 'loop' ) ) }
						onChange={ ( value ) => updateAttributes( { loop: Boolean( value ) } ) }
					/>
				</PanelRow>
			</PanelBody>

			{/* Autoplay Settings */}
			<PanelBody
				title={ __( 'Autoplay Settings', 'wp-before-after-image-slider' ) }
				initialOpen={ true }
			>
				<PanelRow>
					<ToggleControl
						label={ __( 'Enable Autoplay', 'wp-before-after-image-slider' ) }
						help={ __( 'Automatically moves to the next slide after each delay interval.', 'wp-before-after-image-slider' ) }
						checked={ Boolean( getValue( 'autoplay' ) ) }
						onChange={ ( value ) => {
							updateAttributes( { autoplay: Boolean( value ) } );
						} }
					/>
				</PanelRow>

				{ getValue( 'autoplay' ) && (
					<>
						<PanelRow>
							<RangeControl
								label={ __( 'Autoplay Delay (ms)', 'wp-before-after-image-slider' ) }
								help={ __( 'Delay between transitions in milliseconds.', 'wp-before-after-image-slider' ) }
								value={ getValue( 'autoplay_delay' ) }
								onChange={ ( value ) => updateAttributes( { autoplay_delay: value } ) }
								min={ 1000 }
								max={ 10000 }
								step={ 500 }							withInputField={ true }
							renderTooltipContent={ ( value ) => `${ value }ms` }							/>
						</PanelRow>

						<PanelRow>
							<ToggleControl
								label={ __( 'Pause on Hover', 'wp-before-after-image-slider' ) }
								help={ __( 'Pause autoplay when hovering over the carousel.', 'wp-before-after-image-slider' ) }
								checked={ Boolean( getValue( 'pause_on_hover' ) ) }
								onChange={ ( value ) => updateAttributes( { pause_on_hover: Boolean( value ) } ) }
							/>
						</PanelRow>
					</>
				) }
			</PanelBody>

			{/* Navigation Settings */}
			<PanelBody
				title={ __( 'Navigation Settings', 'wp-before-after-image-slider' ) }
				initialOpen={ false }
			>
				<PanelRow>
					<ToggleControl
						label={ __( 'Navigation Arrows', 'wp-before-after-image-slider' ) }
						help={ __( 'Show previous/next navigation arrows.', 'wp-before-after-image-slider' ) }
						checked={ Boolean( getValue( 'navigation' ) ) }
						onChange={ ( value ) => updateAttributes( { navigation: Boolean( value ) } ) }
					/>
				</PanelRow>

				<PanelRow>
					<ToggleControl
						label={ __( 'Pagination', 'wp-before-after-image-slider' ) }
						help={ __( 'Show pagination indicators.', 'wp-before-after-image-slider' ) }
						checked={ Boolean( getValue( 'pagination' ) ) }
						onChange={ ( value ) => updateAttributes( { pagination: Boolean( value ) } ) }
					/>
				</PanelRow>

				{/* { getValue( 'pagination' ) && (
					<PanelRow>
						<SelectControl
							label={ __( 'Pagination Type', 'wp-before-after-image-slider' ) }
							value={ getValue( 'pagination_type' ) }
							onChange={ ( value ) => updateAttributes( { pagination_type: value } ) }
							options={ [
								{ label: __( 'Bullets', 'wp-before-after-image-slider' ), value: 'bullets' },
								{ label: __( 'Fraction (1/5)', 'wp-before-after-image-slider' ), value: 'fraction' },
								{ label: __( 'Progress Bar', 'wp-before-after-image-slider' ), value: 'progressbar' },
							] }
						/>
					</PanelRow>
				) } */}

				{ getValue( 'navigation' ) && (
					<>
						<PanelRow>
							<div className="bais-section-divider">
								<span>{ __( 'Navigation Styles', 'wp-before-after-image-slider' ) }</span>
							</div>
						</PanelRow>

						<PanelRow>
							<div className="field-control field-inline">
								<span className="field-control__label">{ __( 'Normal State', 'wp-before-after-image-slider' ) }</span>
								<div className="fields-wrapper inline">
									<div className="field">
										<CarouselColorControl
											attributes={ attributes }
											updateAttributes={ updateAttributes }
											attribute="nav_bg_color"
											labelText={ __( 'Background', 'wp-before-after-image-slider' ) }
											resetText={ __( 'Clear', 'wp-before-after-image-slider' ) }
										/>
									</div>
									<div className="field">
										<CarouselColorControl
											attributes={ attributes }
											updateAttributes={ updateAttributes }
											attribute="nav_arrow_color"
											labelText={ __( 'Arrow', 'wp-before-after-image-slider' ) }
											resetText={ __( 'Clear', 'wp-before-after-image-slider' ) }
										/>
									</div>
								</div>
							</div>
						</PanelRow>

						<PanelRow>
							<div className="field-control field-inline">
								<span className="field-control__label">{ __( 'Hover State', 'wp-before-after-image-slider' ) }</span>
								<div className="fields-wrapper inline">
									<div className="field">
										<CarouselColorControl
											attributes={ attributes }
											updateAttributes={ updateAttributes }
											attribute="nav_hover_bg_color"
											labelText={ __( 'Background', 'wp-before-after-image-slider' ) }
											resetText={ __( 'Clear', 'wp-before-after-image-slider' ) }
										/>
									</div>
									<div className="field">
										<CarouselColorControl
											attributes={ attributes }
											updateAttributes={ updateAttributes }
											attribute="nav_hover_arrow_color"
											labelText={ __( 'Arrow', 'wp-before-after-image-slider' ) }
											resetText={ __( 'Clear', 'wp-before-after-image-slider' ) }
										/>
									</div>
								</div>
							</div>
						</PanelRow>
					</>
				) }

				{ getValue( 'pagination' ) && (
					<>
						<PanelRow>
							<div className="bais-section-divider">
								<span>{ __( 'Pagination Styles', 'wp-before-after-image-slider' ) }</span>
							</div>
						</PanelRow>

						<PanelRow>
							<div className="field-control field-inline">
								<span className="field-control__label">{ __( 'Colors', 'wp-before-after-image-slider' ) }</span>
								<div className="fields-wrapper inline">
									<div className="field">
										<CarouselColorControl
											attributes={ attributes }
											updateAttributes={ updateAttributes }
											attribute="pag_bg_color"
											labelText={ __( 'Default', 'wp-before-after-image-slider' ) }
											resetText={ __( 'Clear', 'wp-before-after-image-slider' ) }
										/>
									</div>
									<div className="field">
										<CarouselColorControl
											attributes={ attributes }
											updateAttributes={ updateAttributes }
											attribute="pag_active_color"
											labelText={ __( 'Active', 'wp-before-after-image-slider' ) }
											resetText={ __( 'Clear', 'wp-before-after-image-slider' ) }
										/>
									</div>
									<div className="field">
										<CarouselColorControl
											attributes={ attributes }
											updateAttributes={ updateAttributes }
											attribute="pag_hover_color"
											labelText={ __( 'Hover', 'wp-before-after-image-slider' ) }
											resetText={ __( 'Clear', 'wp-before-after-image-slider' ) }
										/>
									</div>
								</div>
							</div>
						</PanelRow>
					</>
				) }

			</PanelBody>

			{/* Responsive Settings */}
			<PanelBody
				title={ __( 'Responsive Settings', 'wp-before-after-image-slider' ) }
				initialOpen={ false }
			>
				<PanelRow>
					<p className="bais-responsive-description">
						{ __( 'Configure slides per view and spacing for different screen sizes.', 'wp-before-after-image-slider' ) }
					</p>
				</PanelRow>

				{/* Mobile */}
				<PanelRow>
					<BreakpointRow
						title={ __( 'Mobile', 'wp-before-after-image-slider' ) }
						description={ __( 'Default for all screens.', 'wp-before-after-image-slider' ) }
						breakpointLabel={ __( 'Up to:', 'wp-before-after-image-slider' ) }
						breakpointValue={ getValue( 'breakpoint_mobile' ) }
						breakpointMin={ 300 }
						breakpointMax={ 800 }
						onBreakpointChange={ ( value ) => updateAttributes( { breakpoint_mobile: parseInt( value, 10 ) || 640 } ) }
						slidesValue={ getValue( 'slides_mobile' ) }
						slidesMax={ 5 }
						onSlidesChange={ ( value ) => updateAttributes( { slides_mobile: parseInt( value, 10 ) || 1 } ) }
						spaceValue={ getValue( 'space_mobile' ) }
						onSpaceChange={ ( value ) => updateAttributes( { space_mobile: parseInt( value, 10 ) || 10 } ) }
					/>
				</PanelRow>

				{/* Tablet */}
				<PanelRow>
					<BreakpointRow
						title={ __( 'Tablet', 'wp-before-after-image-slider' ) }
						description={ __( 'Medium screens.', 'wp-before-after-image-slider' ) }
						breakpointLabel={ __( 'From:', 'wp-before-after-image-slider' ) }
						breakpointValue={ getValue( 'breakpoint_tablet' ) }
						breakpointMin={ 600 }
						breakpointMax={ 1200 }
						onBreakpointChange={ ( value ) => updateAttributes( { breakpoint_tablet: parseInt( value, 10 ) || 768 } ) }
						slidesValue={ getValue( 'slides_tablet' ) }
						slidesMax={ 8 }
						onSlidesChange={ ( value ) => updateAttributes( { slides_tablet: parseInt( value, 10 ) || 2 } ) }
						spaceValue={ getValue( 'space_tablet' ) }
						onSpaceChange={ ( value ) => updateAttributes( { space_tablet: parseInt( value, 10 ) || 20 } ) }
					/>
				</PanelRow>

				{/* Desktop */}
				<PanelRow>
					<BreakpointRow
						title={ __( 'Desktop', 'wp-before-after-image-slider' ) }
						description={ __( 'Large screens.', 'wp-before-after-image-slider' ) }
						breakpointLabel={ __( 'From:', 'wp-before-after-image-slider' ) }
						breakpointValue={ getValue( 'breakpoint_desktop' ) }
						breakpointMin={ 800 }
						breakpointMax={ 2000 }
						onBreakpointChange={ ( value ) => updateAttributes( { breakpoint_desktop: parseInt( value, 10 ) || 1024 } ) }
						slidesValue={ getValue( 'slides_desktop' ) }
						slidesMax={ 10 }
						onSlidesChange={ ( value ) => updateAttributes( { slides_desktop: parseInt( value, 10 ) || 3 } ) }
						spaceValue={ getValue( 'space_desktop' ) }
						onSpaceChange={ ( value ) => updateAttributes( { space_desktop: parseInt( value, 10 ) || 30 } ) }
					/>
				</PanelRow>
			</PanelBody>
		</Panel>
	);
};

export default SettingsTab;
